import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  BookOpen,
  CheckCircle,
  Clock,
  ExternalLink,
  Link as LinkIcon,
  MapPin,
  Target,
} from "react-feather";
import { Banner, Input, Section, Spinner } from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../strings";
import AnchorConfirmContent from "./AnchorConfirmContent";
import GamebookPickerTable from "./GamebookPickerTable";
import { parseIconikAssetUrl } from "./iconikUrls";
import {
  filmsGroundMutationErrorMessage,
  useAnchorJob,
  useSubmitStart,
} from "./service";

/**
 * Films Ground's "set the game start / confirm anchors" surface — set the game start (Phase A),
 * then confirm the auto-suggested anchors (Phase B), both under one mount. Structured to match
 * `films-ground-set-start-mockup.html` (two-column layout with an explainer rail, the "start"
 * anchor-card treatment for the boundary form) for the awaiting_start step, and
 * `films-ground-anchor-review-mockup.html` (via `AnchorConfirmContent`) for the confirm step. Not
 * reproduced: the mockup's own collection-context breadcrumb strip (Point 6, chrome rework — the
 * rail is the only job-identity wayfinding now), per-camera anchor-progress badges
 * (`AnchorJobSummary` has no confirmed count per job — would be a data-model change), and the
 * mockups' own demo-timer/DOM-script logic (this screen's `analyzing` status card is driven by
 * real polling instead).
 *
 * TOP FILMS (`job.mode === "top_films"`) reuses this SAME surface for its one and only step — the
 * `awaiting_start` layout below is identical UI for both modes, just with different copy (`copy`,
 * merged from `strings.tsx`'s `phaseATopFilms` overrides) since Top's has no Step 2 to reference.
 * A Top Films job's `POST .../start` goes straight to `status: "submitted"`, so it never reaches
 * `analyzing`/`needs_confirmation`/`AnchorConfirmContent` at all — those stay FG-only.
 *
 * ONE-PAGE RAIL (design A4, Monday 12936679727, supersedes the earlier "consolidated into the
 * Control Room" design this component briefly had — `AnchorJobPicker`'s org-wide collection tab
 * strip is gone; `ControlRoomScreen.tsx`'s own rail is now the ONLY job navigation). The strip's
 * per-job status badge (Awaiting start / Analyzing / Ready to confirm / Submitted) is NOT this
 * panel's concern either way — it never rendered that badge itself even when the strip was still
 * a sibling of this panel; it lives entirely in `ControlRoomScreen.tsx`'s rail row now (round-4
 * review finding: an earlier version of this comment implied the badge move was covered by "the
 * strip is gone", which it wasn't — see that file's `ReviewStatusBadge` for where it actually
 * landed). This is a PANEL, not a page — it owns none of the app chrome (`SpotterHeader`, the
 * org-access gate) itself, and is mounted by `ControlRoomScreen.tsx` at
 * `spotter/control-room/:jobId` with a REQUIRED `jobId` prop (the "nothing selected" state is the
 * parent's own concern, rendered instead of this panel when no job is selected — see that file's
 * doc comment). Mounted with `key={jobId}` by the parent, so switching jobs remounts this panel
 * fresh rather than needing to manually reset `startUrl`/poll state itself.
 * `AppRouter.tsx`'s old `spotter/anchor-review(/:jobId)` routes still redirect into
 * `spotter/control-room(/:jobId)` — a resume-url already in the wild keeps working.
 *
 * The `awaiting_start` layout below keeps its own two-column shape deliberately — everything else
 * about it (border, spacing) matches `AnchorConfirmContent.tsx`'s own styled-components, same
 * conventions the rest of this panel uses.
 */
const ANALYSIS_POLL_TIMEOUT_MS = 30 * 60 * 1000;

/** `../gamebook/GamebookScreen.tsx`'s `Wrap` token-alias block, copied verbatim — re-skins every
 *  `@cinesys/ui` control to the Spotter palette. No feature-specific tokens needed on top of it
 *  (`--spotterAccentSoft` etc. already exist in `SpotterThemeProvider.tsx`). Shared (via
 *  `TOKEN_ALIASES`) between the layout shells below rather than duplicated. */
const TOKEN_ALIASES = `
  --inputBackground: var(--spotterRaised);
  --inputBorder: var(--spotterLine2);
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
  --blue060: var(--spotterAccent);
  --blue100: var(--spotterAccent);
  --selectOptionHover: var(--spotterAccentHover);
  --blue400: var(--spotterAccent);
  --blue500: var(--spotterAccent);
  --blueBorder: var(--spotterAccentLine);
  --blueBorderOverlay: var(--spotterAccentLine);
  --blueBackground: var(--spotterAccentSoft);
  --blueFocusRing: var(--spotterAccentSoft);
  --green400: var(--spotterOk);
  --green500: var(--spotterOk);
  --greenBorder: var(--spotterOkLine);
  --greenBorderOverlay: var(--spotterOkLine);
  --greenBackground: var(--spotterOkSoft);
  --red400: var(--spotterDanger);
  --red500: var(--spotterDanger);
  --redBorder: var(--spotterDangerBorder);
  --redBorderOverlay: var(--spotterDangerBorder);
  --redBackground: var(--spotterDangerSoft);
`;

/** Narrow shell — used for the empty state and the two remaining terminal status cards
 *  (analyzing/submitted-with-no-anchors; a `needs_confirmation` job goes straight into
 *  `AnchorConfirmContent` instead of a status card of its own — see `showConfirmContent` below).
 *  No outer page padding (the standalone route's version had `padding: 20px 22px 40px`) —
 *  `ControlRoomScreen` supplies that around this whole panel instead. */
const Wrap = styled.div`
  ${TOKEN_ALIASES}
`;

/** Wide two-column shell for the `awaiting_start` step — main content + a 340px explainer rail,
 *  mirrors the mockup's own `.layout` grid (main + 340px `.rail`) exactly. */
const StartLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
  align-items: start;
  ${TOKEN_ALIASES}

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const Rail = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RailHelp = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--spotterInkSoft);
`;

const Intro = styled.div`
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 18px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const IntroIcon = styled.div`
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: var(--spotterAccentSoft);
  color: var(--spotterAccent);
`;

const IntroTitle = styled.h1`
  font-size: 15px;
  margin: 0 0 4px;
  font-weight: 650;
`;

// Point 13: no width clamp, matching AnchorConfirmContent.tsx's own IntroBody (Point 7) — same
// card, same treatment, both fill the intro card's real width rather than wrapping narrow.
const IntroBody = styled.p`
  margin: 0;
  color: var(--spotterInkSoft);
  font-size: 13px;
`;

/** `films-ground-set-start-mockup.html`'s `.section-label` — a small uppercase step header with a
 *  trailing rule filling the remaining row width. */
const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 2px 12px;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
`;

const SectionLabelLine = styled.span`
  flex: 1;
  height: 1px;
  background: var(--spotterLine);
`;

/** The start-boundary "anchor card" — same floating-border recipe as Gamebook's `MatchupCard`
 *  (plain border, no left accent). Unlike a real anchor card, this one has no resolved/out-of-order
 *  state of its own — the whole `StartCard` unmounts the
 *  moment a play is picked (the job's status moves off `awaiting_start`) — so it only ever needs
 *  the neutral/pending border variant, never green or red. */
const StartCard = styled.div`
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 13px;
  overflow: hidden;
  box-shadow: var(--spotterShadow);
`;

const StartCardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
`;

const StartIdx = styled.span`
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: var(--spotterAccentSoft);
  color: var(--spotterAccent);
`;

const StartCardTitle = styled.div`
  font-weight: 600;
  font-size: 13.5px;
`;

const StartCardSubtitle = styled.span`
  font-size: 12px;
  color: var(--spotterInkFaint);
`;

const HeadSpacer = styled.span`
  flex: 1;
`;

const StartChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  background: var(--spotterAccentSoft);
  color: var(--spotterAccent);
`;

const StartCardBody = styled.div`
  padding: 2px 18px 18px;
`;

const FullPrompt = styled.p`
  max-width: 76ch;
  margin: 2px 0 14px;
  font-size: 13px;
  color: var(--spotterInkSoft);

  b {
    color: var(--spotterInk);
    font-weight: 600;
  }
`;

const LinkRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

/** `films-ground-set-start-mockup.html`'s `.ext-link` chip. Not `@cinesys/ui`'s `Button` with its
 *  `link` prop — that renders a react-router `<Link>` for in-app navigation, not a real anchor,
 *  so it can't open an external iconik URL (or a same-app tab in a NEW window, which is the point
 *  here — leaving this one via an in-app nav would drop whatever the customer's already typed
 *  into the start-URL field below). */
const ExtLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 13px;
  border-radius: 9px;
  background: var(--spotterRaised);
  border: 1px solid var(--spotterLine);
  color: var(--spotterInk);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.13s, color 0.13s;

  &:hover {
    border-color: var(--spotterAccent);
    color: var(--spotterAccent);
  }
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  margin-bottom: 7px;
  color: var(--spotterInk);
`;

const StepTwoPrompt = styled.p`
  font-size: 12.5px;
  color: var(--spotterInkSoft);
  margin: 18px 0 10px;
`;

// No title prop anywhere this renders — Section checks title !== undefined, not truthiness, so
// the old title="" here rendered a genuinely empty title bar (its own border-bottom) as a stray
// divider above the card.
const StatusCard = styled(Section)`
  text-align: center;
  max-width: 520px;
  margin: 40px auto;
`;

const StatusIcon = styled.div<{ $tone?: "flag" }>`
  width: 48px;
  height: 48px;
  margin: 0 auto 18px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ $tone }) =>
    $tone === "flag" ? "var(--spotterFlagSoft)" : "var(--spotterOkSoft)"};
  color: ${({ $tone }) =>
    $tone === "flag" ? "var(--spotterFlag)" : "var(--spotterOk)"};
`;

const StatusTitle = styled.div`
  font-size: 18px;
  font-weight: 650;
  margin-bottom: 8px;
`;

const StatusBody = styled.p`
  font-size: 13.5px;
  color: var(--spotterInkSoft);
  line-height: 1.55;
  margin: 0 auto 20px;
  max-width: 46ch;
`;

export interface AnchorReviewPanelProps {
  jobId: string;
}

/** `ControlRoomScreen.tsx` mounts this with `key={jobId}` — a full remount on job switch, so
 *  every state hook below starts fresh per job with no manual reset effect needed. */
const AnchorReviewPanel = ({ jobId }: AnchorReviewPanelProps) => {
  const t = SPOTTER_STRINGS.filmsGround;

  const [startUrl, setStartUrl] = useState("");

  const [polling, setPolling] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [analysisStartedAt, setAnalysisStartedAt] = useState<number | null>(
    null
  );

  const jobQuery = useAnchorJob(jobId, polling && !pollTimedOut);
  const job = jobQuery.data;

  // Poll/timeout state machine — simpler than Gamebook's own (`GamebookScreen.tsx`): Films
  // Ground's `status` is itself a clean terminal signal (analyzing -> needs_confirmation OR
  // submitted, no ambiguous "stays completed throughout a refresh" case), so there's no need for
  // Gamebook's `baselineGeneratedAt` bookkeeping — just "poll while status===analyzing, stop on
  // any other status or timeout". Re-runs on every poll tick (`dataUpdatedAt`) since react-query
  // v3's structural sharing can return the SAME `job` object reference across ticks when nothing
  // changed, which wouldn't otherwise re-trigger this effect.
  useEffect(() => {
    if (!job) {
      return;
    }
    if (job.status === "analyzing") {
      if (!polling) {
        setPolling(true);
        setPollTimedOut(false);
        setAnalysisStartedAt(Date.now());
      } else if (
        analysisStartedAt !== null &&
        Date.now() - analysisStartedAt > ANALYSIS_POLL_TIMEOUT_MS
      ) {
        setPollTimedOut(true);
      }
    } else if (polling) {
      setPolling(false);
      setPollTimedOut(false);
      setAnalysisStartedAt(null);
    }
  }, [job, jobQuery.dataUpdatedAt, polling, analysisStartedAt]);

  const submitStartMutation = useSubmitStart(jobId);

  const parsedAssetId = parseIconikAssetUrl(startUrl);
  const showStartPicker = startUrl.trim().length > 0;
  const startUrlInvalid = showStartPicker && !parsedAssetId;

  if (jobQuery.isLoading) {
    return <Spinner padTop />;
  }

  const isTopFilms = job?.mode === "top_films";
  // Top Films' copy overrides onto the shared phaseA strings (see strings.tsx's own doc comment
  // on `phaseATopFilms`) — everything not listed there (the start card itself, its fields) is
  // identical across both.
  const copy = isTopFilms ? { ...t.phaseA, ...t.phaseATopFilms } : t.phaseA;

  // The confirm step renders inline (see file doc comment) the moment a job reaches
  // `needs_confirmation` — no separate "ready to confirm" launch card in between (Addison: "it
  // should just go straight to the full page thing") — OR immediately (read-only) once a job is
  // already `submitted` with real anchors to show — a `submitted` job with an EMPTY anchor list
  // means analysis resolved everything on its own with nothing to review, which keeps the simpler
  // "Anchors set automatically" message instead of an empty card list. Top Films never has
  // anchors to show (its `POST .../start` never populates them — see `completeTopFilmsStart`'s
  // doc comment), so `isTopFilms` here is belt-and-suspenders, not the load-bearing check.
  const showConfirmContent =
    !!job &&
    !isTopFilms &&
    (job.status === "needs_confirmation" ||
      (job.status === "submitted" && job.anchors.length > 0));

  return (
    <>
      {job?.status === "awaiting_start" ? (
        <StartLayout>
          <Main>
            {jobQuery.isError && <Banner error={t.loadError} padBottom />}

            <Intro>
              <IntroIcon aria-hidden="true">
                <MapPin size={18} />
              </IntroIcon>
              <div>
                <IntroTitle>{copy.introTitle}</IntroTitle>
                <IntroBody>{copy.introBody}</IntroBody>
              </div>
            </Intro>

            <SectionLabel>
              <span>{copy.stepLabel}</span>
              <SectionLabelLine />
            </SectionLabel>

            <StartCard>
              <StartCardHead>
                <StartIdx aria-hidden="true">
                  <Target size={16} />
                </StartIdx>
                <div>
                  <StartCardTitle>
                    {copy.startBoundaryTitle}{" "}
                    <StartCardSubtitle>
                      — {copy.startBoundarySubtitle}
                    </StartCardSubtitle>
                  </StartCardTitle>
                </div>
                <HeadSpacer />
                <StartChip>{copy.startBoundaryChip}</StartChip>
              </StartCardHead>

              <StartCardBody>
                <FullPrompt>{copy.startCardPrompt}</FullPrompt>

                <LinkRow>
                  {job.source.url && (
                    <ExtLink
                      href={job.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkIcon size={14} />
                      {job.source.kind === "collection"
                        ? copy.openCollectionLink
                        : copy.openSavedSearchLink}
                      <ExternalLink size={12} />
                    </ExtLink>
                  )}
                  <ExtLink
                    href="/spotter/gamebook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BookOpen size={14} />
                    {copy.openGamebookLink}
                    <ExternalLink size={12} />
                  </ExtLink>
                </LinkRow>

                <Field>
                  <FieldLabel>
                    {copy.startUrlLabel} · {copy.startUrlRequired}
                  </FieldLabel>
                  <Input
                    icon={LinkIcon}
                    value={startUrl}
                    onChange={setStartUrl}
                    placeholder={copy.startUrlPlaceholder}
                  />
                  {startUrlInvalid && (
                    <Banner error={copy.startUrlInvalid} padTop />
                  )}
                </Field>

                {showStartPicker && parsedAssetId && (
                  <>
                    <StepTwoPrompt>{copy.pickPlayPrompt}</StepTwoPrompt>
                    <GamebookPickerTable
                      plays={job.firstTenPlays}
                      onSelect={(play) =>
                        submitStartMutation.mutate({
                          assetId: parsedAssetId,
                          uid: play.uid,
                        })
                      }
                    />
                    {submitStartMutation.isLoading && <Spinner padTop />}
                    {submitStartMutation.isError && (
                      <Banner
                        error={filmsGroundMutationErrorMessage(
                          submitStartMutation.error,
                          copy.submitStartError
                        )}
                        padTop
                      />
                    )}
                  </>
                )}
              </StartCardBody>
            </StartCard>
          </Main>

          <Rail>
            <Section title={copy.railStepTitle} pad noMargin>
              <RailHelp>{copy.railStepBody}</RailHelp>
            </Section>
            <Section title={copy.railNextTitle} pad noMargin>
              <RailHelp>{copy.railNextBody}</RailHelp>
            </Section>
          </Rail>
        </StartLayout>
      ) : showConfirmContent && job ? (
        <AnchorConfirmContent job={job} />
      ) : (
        <Wrap>
          {jobQuery.isError && <Banner error={t.loadError} padBottom />}

          {/* Both cards below are FG's own Phase-B-pending states — unreachable for a Top Films
              job by construction (its POST .../start writes "submitted" directly, and n8n never
              PUTs anchors for one), but gated on `!isTopFilms` explicitly rather than relying on
              that alone. */}
          {job?.status === "analyzing" && !isTopFilms && (
            <StatusCard pad>
              {/* A check, not a spinner (mockup's own `.ao-check` analysis-gate overlay) — this
                  step is meant to read as "got it, sit tight", not "something's actively
                  loading" (there's nothing on this screen for the customer to wait ON; the
                  actual analysis happens out-of-band and this tab just polls quietly). */}
              <StatusIcon $tone={pollTimedOut ? "flag" : undefined}>
                {pollTimedOut ? <Clock size={22} /> : <CheckCircle size={22} />}
              </StatusIcon>
              <StatusTitle>{copy.analysisUnderwayTitle}</StatusTitle>
              <StatusBody>
                {pollTimedOut
                  ? copy.analysisTimeout
                  : copy.analysisUnderwayBody}
              </StatusBody>
            </StatusCard>
          )}

          {job?.status === "submitted" && (
            <StatusCard pad>
              <StatusIcon>
                <CheckCircle size={22} />
              </StatusIcon>
              <StatusTitle>{copy.autoSubmittedTitle}</StatusTitle>
              <StatusBody>{copy.autoSubmittedBody}</StatusBody>
            </StatusCard>
          )}
        </Wrap>
      )}
    </>
  );
};

export default AnchorReviewPanel;
